import React, { useState, useEffect, useContext } from "react";
import {
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  Divider,
  Chip,
  Button,
  MenuItem,
  IconButton,
  Menu,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { DataGrid } from "@mui/x-data-grid";
import { AppContext } from "../context/AppProvider";
import { AuthContext } from "../context/AuthProvider";
import { cloneDeep } from "lodash";
import { fetchWithInterceptor } from "../utils/fetchWithInterceptor";
import ContainerOptionFormDialog from "../sections/container-options/ContainerOptionFormDialog";
import ConfirmDialog from "../components/ConfirmDialog";

const PAGE_SIZE = 20;

const DEFAULT_FILTER = { name: "", isActive: "" };

export default function ContainerOptionsPage() {
  const appContext = useContext(AppContext);
  const authContext = useContext(AuthContext);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [filters, setFilters] = useState(DEFAULT_FILTER);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleReset = () => setFilters(cloneDeep(DEFAULT_FILTER));

  const handleSaveContainerOption = async (payload) => {
    try {
      const method = selectedRow ? "PUT" : "POST";
      const url = selectedRow
        ? `/api/container-options/${selectedRow.id}`
        : "/api/container-options";
      const res = await fetchWithInterceptor(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          "x-access-token": authContext.getToken(),
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Failed to save container option");
      }
      appContext.showMessage(
        selectedRow ? "Container option updated successfully" : "Container option created successfully",
        "success"
      );
      setDialogOpen(false);
      loadContainerOptions(true);
    } catch (err) {
      console.error(err);
      appContext.showMessage(err.message || "Error saving container option", "error");
    }
  };

  const handleToggleStatus = async () => {
    if (!selectedRow) return;
    try {
      const newStatus = !selectedRow.isActive;
      const res = await fetchWithInterceptor(
        `/api/container-options/${selectedRow.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "x-access-token": authContext.getToken(),
          },
          body: JSON.stringify({ isActive: newStatus }),
        }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Failed updating status");
      }
      appContext.showMessage(
        newStatus ? "Container option activated" : "Container option deactivated",
        "success"
      );
      setAnchorEl(null);
      setSelectedRow(null);
      loadContainerOptions(true);
    } catch (err) {
      console.error(err);
      appContext.showMessage(err.message || "Error updating status", "error");
    }
  };

  const handleMenuOpen = (e, row) => {
    setAnchorEl(e.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleEdit = (row) => {
    setSelectedRow(row);
    setDialogOpen(true);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const loadContainerOptions = async (resetPage = false) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.name) params.append("name", filters.name);
      if (filters.isActive !== "") params.append("isActive", filters.isActive);
      params.append("limit", String(PAGE_SIZE));
      params.append(
        "offset",
        String(resetPage ? 0 : paginationModel.page * PAGE_SIZE)
      );

      const response = await fetchWithInterceptor(
        `/api/container-options?${params.toString()}`,
        {
          headers: {
            "Content-Type": "application/json",
            "x-access-token": authContext.getToken(),
          },
        }
      );
      if (!response.ok) throw new Error("Failed to fetch container options");
      const data = await response.json();
      setRows(data.rows || []);
      setRowCount(data.total ?? 0);
      if (resetPage) {
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
      }
    } catch (err) {
      console.error(err);
      appContext.showMessage("Error loading container options", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContainerOptions(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page]);

  useEffect(() => {
    loadContainerOptions(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const columns = [
    {
      field: "name",
      headerName: "NAME",
      flex: 1,
    },
    {
      field: "isActive",
      headerName: "STATUS",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.isActive ? "Active" : "Inactive"}
          color={params.row.isActive ? "success" : "default"}
          size="small"
        />
      ),
    },
    {
      field: "actions",
      headerName: "ACTIONS",
      width: 150,
      renderCell: (params) => (
        <>
          <Button
            size="small"
            variant="text"
            sx={{ mr: 1, minWidth: "auto", p: 0.5 }}
            onClick={() => handleEdit(params.row)}
          >
            EDIT
          </Button>
          <IconButton size="small" onClick={(e) => handleMenuOpen(e, params.row)}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </>
      ),
    },
  ];

  return (
    <>
      <Typography variant="body1" sx={{ ml: 3, mt: 3, mr: 5, mb: 1, color: "text.secondary" }}>
        Create, edit, and deactivate container options (e.g., "Bottle" / "Box" / "Can" / "Keg"). Changes made here will be consumed by other modules in the ZYN web app (e.g., product creation).
      </Typography>

      <Container sx={{ pb: 3 }} component="main" maxWidth="100%">
        <Paper
          sx={{
            width: "100%",
            backgroundColor: "background.paper",
            mt: 2,
            mb: 2,
          }}
        >
          <div style={{ padding: 16 }}>
            <Typography variant="h6">Search Filters</Typography>
            <br />
            <Grid container spacing={2} alignItems="center" sx={{ px: 0 }}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  label="Name"
                  name="name"
                  variant="outlined"
                  fullWidth
                  autoComplete="off"
                  value={filters.name}
                  onChange={handleFilterChange}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  select
                  label="Status"
                  name="isActive"
                  variant="outlined"
                  fullWidth
                  value={filters.isActive}
                  onChange={handleFilterChange}
                >
                  <MenuItem value="">ALL</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </TextField>
              </Grid>
              <Grid
                size={{ xs: 12, md: 2 }}
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                }}
              >
                <Button onClick={handleReset}>RESET FILTER</Button>
              </Grid>
            </Grid>
          </div>

          <Divider />

          <Grid container sx={{ px: 4, pb: 2, pt: 2, justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setSelectedRow(null);
                setDialogOpen(true);
              }}
            >
              CREATE CONTAINER OPTION
            </Button>
          </Grid>

          <DataGrid
            rows={rows}
            columns={columns}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            rowCount={rowCount}
            pageSizeOptions={[PAGE_SIZE]}
            disableSelectionOnClick
            disableColumnFilter
            loading={loading}
            getRowId={(row) => row.id}
            sx={{
              height: 52 * PAGE_SIZE + 120,
              border: "none",
              "& .MuiDataGrid-menuIcon": { display: "none" },
              "& .MuiDataGrid-columnHeaderTitle": {
                textTransform: "uppercase",
                fontWeight: 600,
                fontSize: "0.75rem",
                color: "rgba(58, 53, 65, 0.87)",
              },
              "& .MuiDataGrid-columnsContainer": {
                backgroundColor: "rgb(249, 250, 252)",
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: "none",
              },
            }}
          />
        </Paper>
      </Container>

      <ContainerOptionFormDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setSelectedRow(null);
        }}
        onSave={handleSaveContainerOption}
        rowId={selectedRow?.id}
        containerOption={selectedRow}
      />

      <Menu open={!!anchorEl} anchorEl={anchorEl} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            setConfirmOpen(true);
            setAnchorEl(null);
          }}
        >
          {selectedRow?.isActive ? "Deactivate" : "Activate"}
        </MenuItem>
      </Menu>

      <ConfirmDialog
        open={confirmOpen}
        title={selectedRow?.isActive ? "Deactivate" : "Activate"}
        message={
          selectedRow?.isActive ? (
            <>
              Are you sure you want to deactivate
              <strong style={{ fontWeight: 500 }}> "{selectedRow?.name}"?</strong>
            </>
          ) : (
            <>
              Are you sure you want to activate
              <strong style={{ fontWeight: 500 }}> "{selectedRow?.name}"?</strong>
            </>
          )
        }
        onCancel={() => {
          setConfirmOpen(false);
          setAnchorEl(null);
          setSelectedRow(null);
        }}
        onConfirm={async () => {
          setConfirmOpen(false);
          setAnchorEl(null);
          await handleToggleStatus();
          setSelectedRow(null);
        }}
      />
    </>
  );
}
